/**
 * "Get the app": platform detection, the captured install prompt, and the
 * already-installed check. The browser fires beforeinstallprompt once, early,
 * so it is captured at app start (main.tsx), not when Home mounts.
 */
import { useEffect, useState } from 'react';

export type Platform = 'ios-safari' | 'ios-chrome' | 'android' | 'desktop-chrome' | 'desktop-safari' | 'other';

export const PLATFORM_ORDER: Platform[] = ['ios-safari', 'ios-chrome', 'android', 'desktop-chrome', 'desktop-safari'];

/** Best guess at where the player is, from the user agent (iPadOS says "Macintosh", so touch points break the tie). */
export function detectPlatform(ua: string, maxTouchPoints = 0): Platform {
  const iOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
  if (iOS) return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) ? 'ios-chrome' : 'ios-safari';
  if (/Android/.test(ua)) return 'android';
  if (/Edg\/|Chrome\/|Chromium\//.test(ua)) return 'desktop-chrome';
  if (/Safari\//.test(ua) && /Macintosh/.test(ua)) return 'desktop-safari';
  return 'other';
}

export function currentPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  return detectPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    /* matchMedia unavailable */
  }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
let installedFlag = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

/** Call once at app start. */
export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installedFlag = true;
    notify();
  });
}

/** Show the browser's own install dialog. Resolves true if the player accepted. */
export async function promptInstall(): Promise<boolean> {
  const ev = deferred;
  if (!ev) return false;
  deferred = null; // a prompt event can be used once
  notify();
  try {
    await ev.prompt();
    const { outcome } = await ev.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  }
}

export function useInstall(): { installed: boolean; canPrompt: boolean } {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return { installed: installedFlag || isInstalled(), canPrompt: deferred !== null };
}
